import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Divider, Flex, Text, Grid, Button
} from 'components/ui';
import CreditCard from 'components/FrictionlessCheckout/PaymentReleaseFour/CreditCard';
import Radio from 'components/Inputs/Radio/Radio';
import ErrorMsg from 'components/ErrorMsg';
import creditCardUtils from 'utils/CreditCard';
import agentAwareUtils from 'utils/AgentAware';
import EditButton from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList/EditButton';
import RemoveButton from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList/RemoveButton';

function isRadioDisabled(creditCard, disabled) {
    return creditCardUtils.tokenMigrationFailed(creditCard) || disabled;
}

function shouldHideUseThisCard(creditCard, disabled) {
    return creditCard?.isExpired || creditCardUtils.tokenMigrationFailed(creditCard) || disabled;
}

// eslint-disable-next-line complexity
function CreditCardRow({
    creditCard,
    showDivider,
    selectedCreditCardId,
    disabled,
    creditCardIsDefaultPayment,
    showEditRemoveButtons,
    showEditButtonFirst,
    shouldDisplayAddOrDeleteCreditCardButton,
    hideUseThisCardButton,
    locales,
    onSetNewCreditCard,
    onShowEditCreditCardForm,
    onRemoveCreditCard,
    onDeleteCreditCard,
    onUseThisCard
}) {
    const {
        remove, edit, update, gotIt, useThisCard, expiredDefaultCardMsg, expiredNonDefaultCardMsg
    } = locales;

    const isSelected = creditCard.creditCardId === selectedCreditCardId;
    const isExpired = !!creditCard?.isExpired;
    const showSelectedExpiredError = isSelected && creditCardUtils.showExpiredMessage(creditCard);
    const editLabel = isExpired ? update : edit;
    const expiredMessage = creditCard.isDefault ? expiredDefaultCardMsg : expiredNonDefaultCardMsg;

    return (
        <Box data-at={Sephora.debug.dataAt('credit_card_item')}>
            <Grid
                columns={['1fr', '1fr auto']}
                paddingTop={4}
                gap={2}
                alignItems={['start', 'start']}
            >
                <Radio
                    paddingY={0}
                    dotOffset={0}
                    alignItems='center'
                    name='payWithCreditCard'
                    disabled={isRadioDisabled(creditCard, disabled)}
                    checked={isSelected}
                    onClick={onSetNewCreditCard(creditCard)}
                    css={styles.radio}
                    aria-disabled={disabled ? 'true' : undefined}
                >
                    <CreditCard
                        creditCard={creditCard}
                        showDefault={creditCardIsDefaultPayment && creditCard.isDefault}
                        isEditView={true}
                    />
                </Radio>
                {showEditRemoveButtons && creditCardUtils.tokenMigrationDisabledOrSucceed(creditCard) && (
                    <Flex
                        alignItems={['center', 'flex-start']}
                        marginLeft={[9, 0]}
                        marginTop={[1, 0]}
                    >
                        {showEditButtonFirst && (
                            <EditButton
                                creditCard={creditCard}
                                editText={editLabel}
                                showEditButtonFirst={true}
                                editCallback={onShowEditCreditCardForm}
                            />
                        )}
                        {shouldDisplayAddOrDeleteCreditCardButton && (
                            <RemoveButton
                                creditCard={creditCard}
                                removeText={remove}
                                showEditButtonFirst={showEditButtonFirst}
                                removeCallback={onRemoveCreditCard}
                            />
                        )}
                        {!showEditButtonFirst && (
                            <EditButton
                                creditCard={creditCard}
                                editText={editLabel}
                                editCallback={onShowEditCreditCardForm}
                            />
                        )}
                    </Flex>
                )}
            </Grid>
            {showSelectedExpiredError && (
                <ErrorMsg
                    lineHeight='none'
                    marginTop={2}
                    marginBottom={0}
                    marginLeft={[9, 0]}
                    children={expiredMessage}
                />
            )}
            {isSelected && !hideUseThisCardButton && !shouldHideUseThisCard(creditCard, disabled) && (
                <Box
                    marginTop={3}
                    marginBottom={4}
                >
                    <Button
                        variant='primary'
                        width={['100%', '100%', '380px']}
                        onClick={onUseThisCard}
                    >
                        {useThisCard}
                    </Button>
                </Box>
            )}
            {creditCardUtils.tokenMigrationFailed(creditCard) && (
                <Grid
                    columns='1fr auto'
                    fontSize='sm'
                    lineHeight='tight'
                    backgroundColor='nearWhite'
                    alignItems='center'
                    padding={3}
                    borderRadius={2}
                    marginTop={10}
                >
                    <Text>{creditCard.message}</Text>
                    {showEditRemoveButtons && (
                        <Button
                            variant='secondary'
                            size='xs'
                            children={gotIt}
                            onClick={onDeleteCreditCard(creditCard)}
                        />
                    )}
                </Grid>
            )}
            {showDivider && <Divider marginTop={Sephora.isAgent && agentAwareUtils.isAgentAwareSession() ? !showEditRemoveButtons && '1em' : 4} />}
        </Box>
    );
}

const styles = {
    radio: {
        '&:focus-visible': {
            outline: '2px solid #0066cc',
            outlineOffset: '2px'
        }
    }
};

export default wrapFunctionalComponent(CreditCardRow, 'CreditCardRow');
